import { useEffect, useState, useCallback, useMemo } from "react";
import { api, API } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ListChecks, ChatsCircle, Receipt, FileText, ArrowCounterClockwise, PencilSimple,
  DownloadSimple, User, Trash, MagnifyingGlass, X, FunnelSimple, CalendarBlank,
  CheckCircle, Lock, Bank, ClipboardText, Flag, Buildings,
} from "@phosphor-icons/react";

const RTYPE_ICON = {
  inquiry: ChatsCircle,
  remittance: Receipt,
  monthly_item: ListChecks,
  document: FileText,
  year: DownloadSimple,
  qbo: Bank,
  bank_statement: Bank,
  close_period: Lock,
  user: User,
};

const RTYPE_LABEL = {
  inquiry: "Inquiry",
  remittance: "Remittance",
  monthly_item: "Monthly Item",
  document: "Document",
  year: "Year Export",
  qbo: "QuickBooks",
  bank_statement: "Bank Statement",
  close_period: "Close Period",
  user: "User",
};

const ACTION_COLOR = (action) => {
  if (!action) return "text-[#525252]";
  const a = action.toLowerCase();
  if (a.includes("delete") || a.includes("remove")) return "text-[#EF4444]";
  if (a.includes("reset") || a.includes("reopen")) return "text-[#F59E0B]";
  if (a.includes("submit") || a.includes("resolve") || a.includes("clear") || a.includes("sign") || a.includes("post")) return "text-[#10B981]";
  if (a.includes("flag") || a.includes("review")) return "text-[#F59E0B]";
  if (a.includes("export")) return "text-[#002FA7]";
  return "text-[#525252]";
};

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function dayKey(iso) {
  return new Date(iso).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

function moneyFmt(n) {
  if (n == null || n === "") return "";
  const num = typeof n === "number" ? n : parseFloat(n);
  if (isNaN(num)) return "";
  return num.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

// Collapse 3+ consecutive entries with same user + action + resource_type within 5 minutes
function smartCollapse(items) {
  const out = [];
  let i = 0;
  while (i < items.length) {
    const start = items[i];
    let j = i + 1;
    while (
      j < items.length &&
      items[j].user_id === start.user_id &&
      items[j].action === start.action &&
      items[j].resource_type === start.resource_type &&
      Math.abs(new Date(items[j].created_at) - new Date(start.created_at)) <= 5 * 60 * 1000
    ) j++;
    if (j - i >= 3) {
      out.push({ ...start, _collapsed: items.slice(i, j) });
    } else {
      for (let k = i; k < j; k++) out.push(items[k]);
    }
    i = j;
  }
  return out;
}

export default function Activity() {
  const { activeCompanyId, activeCompany } = useCompany();
  const { user } = useAuth();
  const PAGE_SIZE = 200;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [facets, setFacets] = useState({ users: [], actions: [], resource_types: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [expanded, setExpanded] = useState({});
  // Filters
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ user_id: "", action: [], resource_type: [], date_from: "", date_to: "" });

  const loadFacets = useCallback(async () => {
    if (!activeCompanyId) return;
    try {
      const { data } = await api.get(`/activity/facets?company_id=${activeCompanyId}`);
      setFacets(data);
    } catch (e) {
      console.warn("Failed to load activity facets:", e);
    }
  }, [activeCompanyId]);

  const buildQuery = useCallback((offset = 0) => {
    const p = new URLSearchParams({ company_id: activeCompanyId || "" });
    if (q.trim()) p.append("q", q.trim());
    if (filters.user_id) p.append("user_id", filters.user_id);
    if (filters.action.length) p.append("action", filters.action.join(","));
    if (filters.resource_type.length) p.append("resource_type", filters.resource_type.join(","));
    if (filters.date_from) p.append("date_from", filters.date_from);
    if (filters.date_to) {
      // make end-date inclusive: add one day
      const d = new Date(filters.date_to);
      d.setDate(d.getDate() + 1);
      p.append("date_to", d.toISOString().slice(0, 10));
    }
    p.append("limit", String(PAGE_SIZE));
    if (offset > 0) p.append("offset", String(offset));
    return p.toString();
  }, [activeCompanyId, q, filters]);

  const load = useCallback(async () => {
    if (!activeCompanyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/activity/search?${buildQuery(0)}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Failed to load activity");
    } finally { setLoading(false); }
  }, [activeCompanyId, buildQuery]);

  const loadMore = async () => {
    if (loadingMore || loading) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get(`/activity/search?${buildQuery(items.length)}`);
      setItems((prev) => [...prev, ...(data.items || [])]);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Failed to load more");
    } finally { setLoadingMore(false); }
  };

  useEffect(() => { loadFacets(); }, [loadFacets]);
  useEffect(() => {
    const t = setTimeout(load, 250); // debounce typing — also resets to page 1 on any filter change
    return () => clearTimeout(t);
  }, [load]);

  const handleClear = async () => {
    if (!activeCompanyId) return;
    if (!window.confirm(`Permanently delete ALL activity log entries for ${activeCompany?.name}? This can't be undone.`)) return;
    try {
      const { data } = await api.delete(`/activity?company_id=${activeCompanyId}`);
      toast.success(`Cleared ${data.deleted} entries`);
      await load();
      await loadFacets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear");
    }
  };

  const exportCsv = () => {
    const url = `${API}/activity/export.csv?${buildQuery()}`;
    window.open(url, "_blank");
  };

  const toggleArr = (key, val) => {
    setFilters((f) => {
      const cur = f[key] || [];
      return { ...f, [key]: cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val] };
    });
  };
  const resetFilters = () => {
    setQ("");
    setFilters({ user_id: "", action: [], resource_type: [], date_from: "", date_to: "" });
  };

  const activeFilterCount =
    (q ? 1 : 0) +
    (filters.user_id ? 1 : 0) +
    filters.action.length +
    filters.resource_type.length +
    (filters.date_from ? 1 : 0) +
    (filters.date_to ? 1 : 0);

  const collapsed = useMemo(() => smartCollapse(items), [items]);

  // Group by day
  const groups = useMemo(() => {
    const g = {};
    collapsed.forEach((i) => {
      const k = dayKey(i.created_at);
      if (!g[k]) g[k] = [];
      g[k].push(i);
    });
    return g;
  }, [collapsed]);

  return (
    <div data-testid="activity-page" className="p-6 sm:p-8 max-w-[1200px]">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-tag mb-2">Audit trail</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight leading-none">
            Find any event.
          </h1>
          <p className="text-[#525252] mt-3 max-w-xl text-sm">
            {activeCompany?.name ? `${activeCompany.name}. ` : ""}Search by payer, amount, invoice, doc title, or any action — every change is timestamped and attributed.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={exportCsv}
            className="border border-[#E5E5E5] hover:border-[#0A0A0A] text-sm px-3 py-2 flex items-center gap-1.5"
            data-testid="activity-export-csv"
            title="Export current view to CSV"
          >
            <DownloadSimple size={14} weight="duotone" /> CSV
          </button>
          {user?.role === "owner" && total > 0 && (
            <button
              onClick={handleClear}
              className="border border-[#E5E5E5] hover:border-[#DC2626] hover:text-[#DC2626] text-[#525252] px-3 py-2 text-sm flex items-center gap-1.5"
              data-testid="clear-activity-btn"
            >
              <Trash size={14} weight="duotone" /> Clear log
            </button>
          )}
        </div>
      </div>

      {/* Search bar */}
      <div className="surface-card bg-white p-3 mb-4 flex items-center gap-2">
        <MagnifyingGlass size={16} weight="duotone" className="text-[#525252] flex-shrink-0 ml-1" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search payer, amount, invoice #, doc title, action…"
          className="flex-1 bg-transparent text-sm outline-none"
          data-testid="activity-search-input"
        />
        {q && (
          <button onClick={() => setQ("")} className="p-1 text-[#525252] hover:text-[#0A0A0A]" data-testid="activity-clear-q">
            <X size={14} weight="bold" />
          </button>
        )}
        <button
          onClick={() => setShowFilters((s) => !s)}
          className={`px-3 py-1.5 border text-sm flex items-center gap-1.5 ${showFilters || activeFilterCount > 0 ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5]"}`}
          data-testid="activity-toggle-filters"
        >
          <FunnelSimple size={14} weight="duotone" />
          Filters{activeFilterCount > 0 && <span className="ml-1 bg-white text-[#0A0A0A] rounded-full text-[10px] w-4 h-4 flex items-center justify-center">{activeFilterCount}</span>}
        </button>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="surface-card bg-white p-4 mb-4 space-y-4" data-testid="activity-filter-panel">
          <FilterGroup label="Who">
            <select
              value={filters.user_id}
              onChange={(e) => setFilters((f) => ({ ...f, user_id: e.target.value }))}
              className="text-sm border border-[#E5E5E5] px-2 py-1.5 bg-white"
              data-testid="filter-user"
            >
              <option value="">All users</option>
              {facets.users.map((u) => (
                <option key={u.id} value={u.id}>{`${u.name} (${u.role})`}</option>
              ))}
            </select>
          </FilterGroup>

          <FilterGroup label="What kind">
            <div className="flex flex-wrap gap-1.5">
              {facets.resource_types.map((r) => (
                <Chip
                  key={r}
                  active={filters.resource_type.includes(r)}
                  onClick={() => toggleArr("resource_type", r)}
                  testid={`filter-rtype-${r}`}
                >
                  {RTYPE_LABEL[r] || r.replace("_", " ")}
                </Chip>
              ))}
            </div>
          </FilterGroup>

          <FilterGroup label="Action">
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
              {facets.actions.map((a) => (
                <Chip
                  key={a}
                  active={filters.action.includes(a)}
                  onClick={() => toggleArr("action", a)}
                  testid={`filter-action-${a.replace(/[^\w]/g, "-")}`}
                >
                  {a}
                </Chip>
              ))}
            </div>
          </FilterGroup>

          <FilterGroup label="Date range">
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                className="text-sm border border-[#E5E5E5] px-2 py-1.5"
                data-testid="filter-date-from"
              />
              <span className="text-[#525252] text-sm">to</span>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                className="text-sm border border-[#E5E5E5] px-2 py-1.5"
                data-testid="filter-date-to"
              />
              <DatePreset label="Today" onClick={() => {
                const t = new Date().toISOString().slice(0, 10);
                setFilters((f) => ({ ...f, date_from: t, date_to: t }));
              }} />
              <DatePreset label="Last 7 days" onClick={() => {
                const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 7);
                setFilters((f) => ({ ...f, date_from: s.toISOString().slice(0, 10), date_to: e.toISOString().slice(0, 10) }));
              }} />
              <DatePreset label="This month" onClick={() => {
                const n = new Date();
                const s = new Date(n.getFullYear(), n.getMonth(), 1).toISOString().slice(0, 10);
                const e = n.toISOString().slice(0, 10);
                setFilters((f) => ({ ...f, date_from: s, date_to: e }));
              }} />
              <DatePreset label="Last month" onClick={() => {
                const n = new Date();
                const s = new Date(n.getFullYear(), n.getMonth() - 1, 1).toISOString().slice(0, 10);
                const e = new Date(n.getFullYear(), n.getMonth(), 0).toISOString().slice(0, 10);
                setFilters((f) => ({ ...f, date_from: s, date_to: e }));
              }} />
            </div>
          </FilterGroup>

          {activeFilterCount > 0 && (
            <button onClick={resetFilters} className="text-xs text-[#002FA7] hover:underline" data-testid="filter-reset">
              Clear all filters
            </button>
          )}
        </div>
      )}

      {/* Results summary */}
      <div className="text-xs text-[#525252] mb-3 flex items-center gap-2" data-testid="activity-result-count">
        {loading ? "Loading…" : (
          <>
            <Buildings size={12} weight="duotone" />
            {total} event{total === 1 ? "" : "s"}{activeFilterCount > 0 && " match your filters"}
          </>
        )}
      </div>

      {!loading && items.length === 0 && (
        <div className="surface-card bg-white p-10 text-center">
          <div className="font-heading text-lg">No events found.</div>
          <div className="text-sm text-[#525252] mt-1">
            {activeFilterCount > 0 ? "Try clearing some filters." : "Submissions, inquiries, and remittances will be logged here."}
          </div>
        </div>
      )}

      <div className="space-y-6">
        {Object.entries(groups).map(([date, rows]) => (
          <div key={date}>
            <div className="label-tag mb-2 flex items-center gap-1.5"><CalendarBlank size={12} weight="duotone" /> {date}</div>
            <div className="surface-card bg-white divide-y divide-[#E5E5E5]">
              {rows.map((a) => (
                <ActivityRow
                  key={a.id}
                  a={a}
                  expanded={!!expanded[a.id]}
                  onToggle={() => setExpanded((e) => ({ ...e, [a.id]: !e[a.id] }))}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {!loading && items.length < total && (
        <div className="mt-6 flex flex-col items-center gap-2" data-testid="activity-load-more-wrap">
          <div className="text-xs text-[#525252]">
            Showing {items.length.toLocaleString()} of {total.toLocaleString()} events
          </div>
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-sm px-4 py-2 disabled:opacity-50 disabled:cursor-wait transition-colors"
            data-testid="activity-load-more-btn"
          >
            {loadingMore ? "Loading…" : `Load next ${Math.min(PAGE_SIZE, total - items.length)}`}
          </button>
        </div>
      )}
    </div>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div>
      <div className="label-tag text-[10px] mb-2">{label}</div>
      {children}
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-xs px-2.5 py-1 border transition-colors ${
        active ? "bg-[#0A0A0A] border-[#0A0A0A] text-white" : "border-[#E5E5E5] hover:border-[#0A0A0A]"
      }`}
    >
      {children}
    </button>
  );
}

function DatePreset({ label, onClick }) {
  return (
    <button onClick={onClick} className="text-[11px] px-2 py-1 border border-[#E5E5E5] hover:border-[#0A0A0A]">
      {label}
    </button>
  );
}

function ActivityRow({ a, expanded, onToggle }) {
  const Icon = RTYPE_ICON[a.resource_type] || PencilSimple;
  const meta = a.metadata || {};
  const hasStatusChange = a.before_status || a.after_status;
  const groupCount = a._collapsed?.length || 0;

  return (
    <div className="p-4" data-testid={`activity-${a.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 flex items-center justify-center border border-[#E5E5E5] ${ACTION_COLOR(a.action)} flex-shrink-0`}>
          <Icon size={16} weight="duotone" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm flex items-baseline gap-1.5 flex-wrap">
            <span className="font-medium">{a.user_name}</span>
            <span className={`${ACTION_COLOR(a.action)} font-medium`}>{a.action}</span>
            <span className="text-[#525252]">{(RTYPE_LABEL[a.resource_type] || a.resource_type || "").toLowerCase()}</span>
            {groupCount > 0 && (
              <button
                onClick={onToggle}
                className="text-[11px] text-[#002FA7] underline decoration-dotted hover:no-underline"
                data-testid={`expand-group-${a.id}`}
              >
                ×{groupCount} — {expanded ? "collapse" : "expand"}
              </button>
            )}
          </div>

          {/* Detail line */}
          <div className="text-sm text-[#0A0A0A] mt-0.5 break-words">{a.details}</div>

          {/* Metadata badges */}
          {(meta.payer_name || meta.amount != null || meta.invoice_number) && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {meta.payer_name && <Badge>{meta.payer_name}</Badge>}
              {meta.amount != null && <Badge tone="money">{moneyFmt(meta.amount)}</Badge>}
              {meta.invoice_number && <Badge>INV {meta.invoice_number}</Badge>}
              {meta.month && <Badge>{meta.month}</Badge>}
            </div>
          )}

          {/* Status transition */}
          {hasStatusChange && (
            <div className="text-[11px] text-[#525252] mt-1.5 flex items-center gap-1">
              {a.before_status || "—"} <span className="text-[#A3A3A3]">→</span> <span className="font-medium text-[#0A0A0A]">{a.after_status || "—"}</span>
            </div>
          )}

          {/* Footer row */}
          <div className="label-tag text-[10px] mt-1.5 flex items-center gap-2">
            <User size={10} weight="duotone" /> {a.user_role || "—"}
            <span>·</span>
            <span title={new Date(a.created_at).toLocaleString()}>{relTime(a.created_at)}</span>
          </div>

          {/* Collapsed detail */}
          {expanded && groupCount > 0 && (
            <div className="mt-3 border-l-2 border-[#E5E5E5] pl-3 space-y-1.5" data-testid={`group-detail-${a.id}`}>
              {a._collapsed.map((c) => (
                <div key={c.id} className="text-xs text-[#525252]">
                  <span className="text-[#0A0A0A]">{c.details || c.action}</span>
                  <span className="ml-2 text-[10px]">{new Date(c.created_at).toLocaleTimeString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Badge({ children, tone }) {
  return (
    <span className={`text-[11px] px-1.5 py-0.5 border ${tone === "money" ? "border-[#10B981] text-[#10B981]" : "border-[#E5E5E5] text-[#525252]"}`}>
      {children}
    </span>
  );
}
